import Layout from "../components/Layout.tsx";
import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/Table.tsx";
import { Button } from "../components/Button.tsx";
import { Text } from "../components/Text.tsx";
import { FieldGroup } from "../components/Fieldset.tsx";
import { Select } from "../components/Select.tsx";
import { RiCheckLine, RiCloseLine, RiDeleteBinLine } from "@remixicon/react";
import {
  fetchAdministrations,
  putAdministrationApiKey,
  deleteAdministrationApiKey,
} from "../services/ApiService.ts";
import { EasyflexAdministration } from "../types.ts";
import { toast } from "react-hot-toast";
import { Dialog } from "../components/Dialog.tsx";
import LoadingState from "../components/LoadingState.tsx";
import { isReadOnly } from "../config.ts";

const EasyflexAdministrationsConfiguration = () => {
  const [administrations, setAdministrations] = useState<EasyflexAdministration[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState<number | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [isSandboxInput, setIsSandboxInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteCode, setDeleteCode] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadAdministrations = async () => {
    try {
      const data = await fetchAdministrations();
      setAdministrations(data);
    } catch (err) {
      console.error("Failed to fetch administrations:", err);
      toast.error("Kon administraties niet ophalen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdministrations();
  }, []);

  const handleSaveApiKey = async (code: number) => {
    if (!apiKeyInput.trim()) {
      toast.error("Voer een API key in");
      return;
    }

    setSaving(true);
    const response = await putAdministrationApiKey(code, apiKeyInput.trim(), isSandboxInput);
    setSaving(false);

    if (response.error) {
      toast.error(response.error);
      return;
    }

    toast.success("API key opgeslagen en gevalideerd");
    setEditingCode(null);
    setApiKeyInput("");
    await loadAdministrations();
  };

  const handleRemoveApiKey = async () => {
    if (deleteCode === null) return;
    setDeleting(true);
    const response = await deleteAdministrationApiKey(deleteCode);
    setDeleting(false);

    if (response.error) {
      toast.error(response.error);
      return;
    }

    setDeleteCode(null);
    toast.success("API key verwijderd");
    await loadAdministrations();
  };

  const handleStartEditing = (admin: EasyflexAdministration) => {
    setEditingCode(admin.code);
    setApiKeyInput("");
    setIsSandboxInput(admin.isSandbox);
  };

  const handleCancelEditing = () => {
    setEditingCode(null);
    setApiKeyInput("");
  };

  const sandboxAllowed = administrations.some((admin) => admin.sandboxAllowed);

  return (
    <Layout
      title="Administraties"
      description=""
      stepTitle="Easyflex administraties"
      showBackButton={false}
      showNextButton={false}
    >
      <FieldGroup>
        <Text>
          Configureer je Easyflex API keys per administratie. Een geldige API key is
          vereist voordat je een koppeling kunt aanmaken.
        </Text>
        {loading ? (
          <LoadingState />
        ) : (
        <Table dense>
          <TableHead>
            <TableRow>
              <TableHeader>Code</TableHeader>
              <TableHeader>Naam</TableHeader>
              <TableHeader>API key</TableHeader>
              {sandboxAllowed && <TableHeader>Omgeving</TableHeader>}
              <TableHeader>Acties</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {administrations.map((admin) => (
              <TableRow key={admin.code}>
                <TableCell>{admin.code}</TableCell>
                <TableCell>{admin.name}</TableCell>
                <TableCell>
                  {editingCode === admin.code ? (
                    <input
                      type="password"
                      value={apiKeyInput}
                      onChange={(e) => setApiKeyInput(e.target.value)}
                      placeholder="Voer API key in"
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveApiKey(admin.code);
                        if (e.key === "Escape") handleCancelEditing();
                      }}
                    />
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        admin.hasApiKey
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {admin.hasApiKey ? (
                        <>
                          <RiCheckLine className="h-3.5 w-3.5" />
                          Geconfigureerd
                        </>
                      ) : (
                        <>
                          <RiCloseLine className="h-3.5 w-3.5" />
                          Niet geconfigureerd
                        </>
                      )}
                    </span>
                  )}
                </TableCell>
                {sandboxAllowed && (
                  <TableCell>
                    {editingCode === admin.code ? (
                      <Select
                        value={isSandboxInput ? "sandbox" : "production"}
                        onChange={(e) => setIsSandboxInput(e.target.value === "sandbox")}
                      >
                        <option value="production">Productie</option>
                        <option value="sandbox">Sandbox</option>
                      </Select>
                    ) : admin.hasApiKey ? (
                      admin.isSandbox ? "Sandbox" : "Productie"
                    ) : (
                      "-"
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {editingCode === admin.code ? (
                    <div className="flex gap-2">
                      <Button
                        color="primary"
                        onClick={() => handleSaveApiKey(admin.code)}
                        disabled={saving}
                      >
                        {saving ? "Valideren..." : "Opslaan"}
                      </Button>
                      <Button color="dark" onClick={handleCancelEditing} disabled={saving}>
                        Annuleren
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button color="primary" onClick={() => handleStartEditing(admin)} disabled={isReadOnly}>
                        {admin.hasApiKey ? "Wijzigen" : "Configureren"}
                      </Button>
                      {admin.hasApiKey && (
                        <button
                          onClick={() => setDeleteCode(admin.code)}
                          disabled={isReadOnly}
                          className="rounded-lg p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 disabled:pointer-events-none disabled:opacity-50"
                          title="API key verwijderen"
                        >
                          <RiDeleteBinLine className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {administrations.length === 0 && (
              <TableRow>
                <TableCell colSpan={sandboxAllowed ? 5 : 4} className="text-center text-gray-500">
                  Geen administraties gevonden
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        )}
      </FieldGroup>

      <Dialog open={deleteCode !== null} onClose={() => !deleting && setDeleteCode(null)}>
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <RiDeleteBinLine className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">
            API key verwijderen
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Weet je zeker dat je de API key voor administratie{" "}
            <span className="font-medium text-gray-700">
              {administrations.find((a) => a.code === deleteCode)?.name}
            </span>{" "}
            wilt verwijderen? Actieve koppelingen zullen niet meer synchroniseren.
          </p>
          <div className="mt-6 flex w-full gap-3">
            <Button
              color="light"
              className="flex-1"
              onClick={() => setDeleteCode(null)}
              disabled={deleting}
            >
              Annuleren
            </Button>
            <Button
              color="red"
              className="flex-1"
              onClick={handleRemoveApiKey}
              disabled={deleting}
            >
              {deleting ? "Verwijderen..." : "Verwijderen"}
            </Button>
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default EasyflexAdministrationsConfiguration;
